import { useAtomValue } from 'jotai';

import type {
  HygieneInsightsResponse,
  THygieneTaskCode,
} from '@/react-query/insights/types';
import type { TPracticeGroup } from '@/react-query/insights/usePracticeGroups';
import { usePreferences } from '@/react-query/usePreferences';

import {
  isCountsAtom,
  selectedPracticeGroupsAtom,
  selectedPracticesAtom,
  selectedProviderRoleAtom,
  taskTypeAtom,
} from '../atoms';
import type {
  THygienePracticeRates,
  THygieneProviderInsights,
  THygieneProviderRates,
  THygieneRegionRates,
} from '../types';

import {
  buildHygieneTaskPeriods,
  calculateHygieneRatesFromData,
  calculateOverallHygieneRates,
} from './hygieneRates';

export const useCalculateHygieneBenchmark = (
  data?: HygieneInsightsResponse,
) => {
  const taskTypes = useAtomValue(taskTypeAtom);
  const isCounts = useAtomValue(isCountsAtom);

  const providerRates = data?.practices
    ?.flatMap(
      (practice) =>
        practice.hygienists?.map((hygienist) => ({
          ...hygienist,
          practiceId: practice.practiceVideaId,
          practiceName: practice.practiceName,
          providerRole: hygienist?.providerRole,
          estimatedHygieneValues: practice.estimatedHygieneValues,
        })) || [],
    )
    ?.map((provider) =>
      calculateHygieneRatesForProvider(provider, taskTypes, isCounts),
    )
    ?.filter(
      (provider) =>
        provider.productionRatio ||
        provider.production.overallTasks ||
        provider.opportunity.overallTasks,
    )
    ?.map((provider) => provider?.productionRatio)
    ?.filter((productionRatio) => productionRatio !== undefined)
    ?.sort((a, b) => a - b);

  const lowerQuartile = providerRates?.[Math.floor(providerRates.length / 3)];
  const upperQuartile =
    providerRates?.[Math.floor((providerRates.length * 2) / 3)];

  const initProviderRatesDistribution = Object.fromEntries(
    Array.from({ length: 10 }, (_, i) => [i, 0]),
  );

  const providerRatesDistribution = providerRates?.reduce((acc, curr) => {
    const index = curr !== 1 ? Math.floor(curr * 10) : 9;
    acc[index]++;
    return acc;
  }, initProviderRatesDistribution);

  return {
    lowerBenchmark: lowerQuartile,
    upperBenchmark: upperQuartile,
    providerRatesDistribution: providerRatesDistribution,
  };
};

const calculateHygieneRatesForProvider = (
  provider: THygieneProviderInsights,
  taskCodes?: THygieneTaskCode[],
  isCounts?: boolean,
): THygieneProviderRates => {
  const filteredTasks =
    taskCodes && taskCodes.length > 0
      ? Object.fromEntries(
          Object.entries(provider.tasks ?? {}).filter(([key]) =>
            taskCodes.includes(key as THygieneTaskCode),
          ),
        )
      : provider.tasks;

  const { production, opportunity } = buildHygieneTaskPeriods(
    filteredTasks ?? {},
    provider.estimatedHygieneValues,
    isCounts,
  );
  const rates = calculateHygieneRatesFromData(
    {
      production,
      opportunity,
    },
    provider.estimatedHygieneValues,
  );

  return {
    ...rates,
    providerId: provider.providerVideaId ?? provider.providerId.toString(), // fallback because videa id is not available for mocked dev/sales demo data
    providerName: provider.providerName,
    practiceId: provider.practiceId,
    practiceName: provider.practiceName,
    providerRole: provider?.providerRole,
  };
};

// Stateful method that maintains the hygiene row data currently filteredacross insights
// This is the same as useFilteredInsightsData but for hygiene and builds very similar types
export const useFilteredHygieneInsightsData = (
  data: HygieneInsightsResponse | undefined,
  regions: TPracticeGroup[] | undefined,
) => {
  const taskTypes = useAtomValue(taskTypeAtom);
  const selectedRegions = useAtomValue(selectedPracticeGroupsAtom);
  const selectedPractices = useAtomValue(selectedPracticesAtom);
  const selectedProviderRole = useAtomValue(selectedProviderRoleAtom);
  const isCounts = useAtomValue(isCountsAtom);

  const { data: prefs } = usePreferences();
  const enabledTaskTypes = prefs?.insightsEnabledTasks;

  const providerData: THygieneProviderInsights[] =
    data?.practices?.flatMap(
      (practice) =>
        practice.hygienists?.map((hygienist) => ({
          ...hygienist,
          practiceId: practice.practiceVideaId,
          practiceName: practice.practiceName,
          providerRole: hygienist?.providerRole,
          estimatedHygieneValues: practice.estimatedHygieneValues,
        })) || [],
    ) || [];

  // Calculate provider rates
  const providerRates =
    providerData
      ?.map((provider) =>
        calculateHygieneRatesForProvider(provider, taskTypes, isCounts),
      )
      ?.filter(
        (provider) =>
          !selectedPractices.length ||
          selectedPractices.includes(provider.practiceId),
      )
      ?.filter(
        (provider) =>
          !selectedProviderRole.length ||
          (provider.providerRole &&
            selectedProviderRole.includes(provider.providerRole)),
      )
      ?.filter((provider) => provider.productionRatio !== undefined)
      ?.filter(
        (provider) =>
          provider.productionRatio ||
          provider.production.overallTasks ||
          provider.opportunity.overallTasks,
      ) ?? [];

  // group providers by practiceId and txTypes and then summarize for each practice
  const practiceRates: THygienePracticeRates[] =
    data?.practices
      ?.map((practice) => {
        // Get all providers for this practice from our existing providerRates
        const practiceHygienists = providerRates.filter(
          (provider) => provider.practiceId === practice.practiceVideaId,
        );

        // Calculate overall rates for the practice by merging provider rates
        const practiceRate = calculateOverallHygieneRates(
          practiceHygienists,
          enabledTaskTypes,
        );

        return {
          ...practiceRate,
          estimatedHygieneValues: practice.estimatedHygieneValues,
          practiceId: practice.practiceVideaId,
          practiceName: practice.practiceName,
          hygienists: practiceHygienists,
        };
      })
      .filter((practice) =>
        selectedPractices.length > 0
          ? selectedPractices.includes(practice.practiceId)
          : true,
      )
      .filter((practice) => practice.productionRatio !== undefined) ?? [];

  // group practices by region and calculate overall rates for each region
  const regionRates: THygieneRegionRates[] =
    regions
      ?.map((region) => {
        // Get all practices for this region from our existing practiceRates, which are already filtered
        const practices = practiceRates.filter((practice) =>
          region.practices.some(
            (p) => p.practiceVideaId === practice.practiceId,
          ),
        );

        // Calculate overall rates for the practice by merging provider rates
        const regionRate = calculateOverallHygieneRates(
          practices,
          enabledTaskTypes,
        );

        return {
          ...regionRate,
          regionId: region.id,
          regionName: region.name,
          practices,
        };
      })
      .filter((region) =>
        selectedRegions.length > 0
          ? selectedRegions.includes(region.regionId)
          : true,
      )
      .filter((region) => region.productionRatio !== undefined) ?? [];

  return {
    providerRates: providerRates,
    practiceRates: practiceRates,
    regionRates: regionRates,
  };
};
